import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CircularProgress, TextField, Button, Alert } from '@mui/material';
import firebaseService from '../services/FirebaseService';
import { ShareLinkService, SHARE_PERMISSIONS } from '../models/ShareLink';
import { useDraft } from '../contexts/DraftContext';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #121212;
  color: #fff;
  padding: 24px;
`;

const Header = styled.div`
  background-color: #1a1a1a;
  padding: 16px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #e94560;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #888;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const PasswordForm = styled.div`
  max-width: 400px;
  margin: 40px auto;
  padding: 24px;
  background-color: #1a1a1a;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 40px auto;
`;

function SharedDraftView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [draft, setDraft] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  // Get draft context methods
  const { importDraftData } = useDraft();

  useEffect(() => {
    loadSharedDraft();
  }, [token, user]); // Re-load when user logs in

  const loadSharedDraft = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load share link
      const link = await firebaseService.getShareLink(token);

      if (!link) {
        setError('Share link not found. It may have been deleted or the link is incorrect.');
        setLoading(false);
        return;
      }

      setShareLink(link);

      // SIMPLIFIED PERMISSION LOGIC:
      // VIEW: Requires password for security
      // Users can view and edit draft in simulator, but must log in to save changes

      // Check if password is required and not yet provided
      if (link.password && !password) {
        setNeedsPassword(true);
        setLoading(false);
        return;
      }

      // Validate access
      const validation = ShareLinkService.validateAccess(
        link,
        password || null,
        user?.email || null
      );

      if (!validation.valid) {
        setError(validation.errors.join(', '));
        setLoading(false);
        return;
      }

      // Load the draft using the share token
      const draftData = await firebaseService.getDraftByShareToken(token);

      if (!draftData) {
        setError('Draft not found. It may have been deleted.');
        setLoading(false);
        return;
      }

      setDraft(draftData);

      // Load draft data into context so DraftBoard can render it
      if (draftData.scenariosFirstPick || draftData.scenariosSecondPick) {
        importDraftData({
          scenariosFirstPick: draftData.scenariosFirstPick || [],
          scenariosSecondPick: draftData.scenariosSecondPick || [],
          teamName: draftData.teamName || '',
          tournament: draftData.tournament || '',
          stage: draftData.stage || '',
          draftDate: draftData.date || '',
        });
      }

      // Record access (if user is logged in)
      if (user) {
        await firebaseService.recordShareLinkAccess(token, {
          userId: user.uid,
          userName: user.displayName || 'Anonymous',
          email: user.email,
          ip: null,
          timestamp: Date.now(),
        });
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading shared draft:', err);
      setError(err.message || 'Failed to load shared draft');
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!password) {
      setPasswordError('Please enter a password');
      return;
    }

    // Validate password before proceeding
    if (shareLink && shareLink.password !== password) {
      setPasswordError('Incorrect password');
      return;
    }

    setNeedsPassword(false);
    // Reload with the password now set
    await loadSharedDraft();
  };

  // LOADING STATE
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <CircularProgress />
          <p>Loading shared draft...</p>
        </LoadingContainer>
      </Container>
    );
  }

  // NEEDS PASSWORD
  if (needsPassword) {
    return (
      <Container>
        <Header>
          <Title>{shareLink?.title || 'Shared Draft'}</Title>
          <Subtitle>This draft is password protected</Subtitle>
        </Header>
        <PasswordForm>
          <h3>Enter Password</h3>
          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              autoFocus
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ mt: 2 }}
            >
              Access Draft
            </Button>
          </form>
        </PasswordForm>
      </Container>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <Alert severity="error">
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Go to Home
          </Button>
        </ErrorContainer>
      </Container>
    );
  }

  // NO DRAFT DATA
  if (!draft) {
    return (
      <Container>
        <ErrorContainer>
          <Alert severity="info">
            No draft data available.
          </Alert>
        </ErrorContainer>
      </Container>
    );
  }

  // SUCCESS - Redirect to simulator to view draft
  // Different behavior based on permission level:
  // - VIEW ONLY: Show minimal UI with just draft board
  // - CAN EDIT: Full access to simulator
  navigate('/simulator', {
    state: {
      isSharedView: true,
      permission: shareLink.permission,
      shareToken: token
    }
  });

  return null;
}

export default SharedDraftView;
